import { useState, useCallback, useEffect, useRef } from "react";
import { Wifi, WifiOff, Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDisplayStream } from "@/hooks/use-display-stream";
import { useDisplayOverlay } from "@/hooks/use-display-overlay";
import { useBoard, useUpdateBoard } from "@/hooks/use-api";
import { detectDisplayMode, useShellDebug } from "@/hooks/use-shell-debug";
import { DisplayCarousel } from "@/components/display/display-carousel";
import { DisplayExitOverlay } from "@/components/display/display-exit-overlay";
import { DisplayQuickTimingPanel } from "@/components/display/display-quick-timing-panel";
import { Box, Flex, Text } from "@/components/display/display-primitives";
import { useAuthStore } from "@/stores/auth.store";
import {
  type FeedPayload,
  type FeedItem,
  type ArchiveState,
  initArchiveState,
  initializeArchiveWindow,
  advanceArchiveWindow,
  archiveWindowToFeedItems,
  queueItemToFeedItem,
  dateSeed,
} from "@/lib/display-feed";
import {
  type QuickTimingSecState,
  timingSecFromSource,
} from "@/lib/display-timing-settings";
import i18n, { type LanguageCode, SUPPORTED_LANGUAGES } from "@/lib/i18n";
import type { SlideTimingSettings } from "@mem/shared";

export function StatusIndicator({ status, hasContent }: { status: string; hasContent: boolean }) {
  const { t } = useTranslation();

  if (status === "connected") return null;
  if (hasContent && (status === "connecting" || status === "reconnecting")) return null;

  const icons: Record<string, React.ReactNode> = {
    connecting: <Loader size={14} style={{ animation: "spin 1s linear infinite" }} />,
    reconnecting: <WifiOff size={14} />,
    error: <WifiOff size={14} />,
    forbidden: <WifiOff size={14} />,
  };

  const labels: Record<string, string> = {
    connecting: t("display.connecting"),
    reconnecting: t("display.reconnecting"),
    error: t("display.offline"),
    forbidden: t("display.forbidden_status"),
    not_found: t("display.not_found_status"),
  };

  return (
    <Box
      style={{
        position: "absolute",
        top: "1rem",
        right: "1rem",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        borderRadius: "999px",
        backgroundColor: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        color: "rgba(255,255,255,0.7)",
      }}
    >
      {icons[status]}
      <Text size="1" style={{ color: "inherit" }}>
        {labels[status] ?? status}
      </Text>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Box>
  );
}

function EmptyState({ boardName }: { boardName: string }) {
  const { t } = useTranslation();
  return (
    <Flex
      align="center"
      justify="center"
      direction="column"
      gap="4"
      style={{
        width: "100%",
        height: "100%",
        background: "radial-gradient(ellipse at center, #1a1a2e 0%, #0d0d1a 100%)",
      }}
    >
      <Text
        style={{
          fontSize: "clamp(1.5rem, 5vw, 3rem)",
          color: "rgba(255,255,255,0.8)",
          fontWeight: 300,
          fontFamily: "Georgia, serif",
          textAlign: "center",
        }}
      >
        {boardName || t("common.app_name")}
      </Text>
      <Text
        style={{
          fontSize: "clamp(0.85rem, 2vw, 1.2rem)",
          color: "rgba(255,255,255,0.35)",
          textAlign: "center",
        }}
      >
        {t("display.waiting")}
      </Text>
    </Flex>
  );
}

function ForbiddenState() {
  const { t } = useTranslation();
  return (
    <Flex
      align="center"
      justify="center"
      direction="column"
      gap="3"
      style={{ width: "100%", height: "100%", backgroundColor: "#0d0d1a" }}
    >
      <WifiOff size={40} color="rgba(255,255,255,0.3)" />
      <Text
        style={{ fontSize: "1.2rem", color: "rgba(255,255,255,0.4)", fontWeight: 300 }}
      >
        {t("display.forbidden_message")}
      </Text>
    </Flex>
  );
}

function ShellDebugOverlay({ info }: { info: ReturnType<typeof useShellDebug> }) {
  if (!info) return null;

  const rows = [
    ["origin", info.origin],
    ["displayMode", info.displayMode],
    ["fullscreen", String(info.fullscreenActive)],
    ["fullscreenEnabled", String(info.fullscreenEnabled)],
    ["navigator.standalone", String(info.navigatorStandalone)],
    ["inner", `${info.innerWidth} x ${info.innerHeight}`],
    ["client", `${info.clientWidth} x ${info.clientHeight}`],
    [
      "visualViewport",
      info.visualViewportWidth && info.visualViewportHeight
        ? `${info.visualViewportWidth} x ${info.visualViewportHeight}`
        : "n/a",
    ],
    ["vv.offsetTop", info.visualViewportOffsetTop ?? "n/a"],
    ["safeTopPx", info.safeAreaInsetTopPx],
    ["href", info.href],
  ] as const;

  return (
    <Box
      style={{
        position: "absolute",
        top: "1rem",
        left: "1rem",
        zIndex: 80,
        maxWidth: "min(92vw, 42rem)",
        padding: "0.75rem 0.9rem",
        borderRadius: "12px",
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(10,10,10,0.78)",
        backdropFilter: "blur(10px)",
        color: "rgba(255,255,255,0.92)",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "12px",
        lineHeight: 1.45,
        whiteSpace: "normal",
        overflowWrap: "anywhere",
        cursor: "default",
      }}
    >
      <Text as="p" size="1" style={{ margin: 0, marginBottom: "0.5rem", color: "rgba(255,255,255,0.7)" }}>
        shell debug
      </Text>
      {rows.map(([label, value]) => (
        <Box key={label} style={{ display: "grid", gridTemplateColumns: "9rem 1fr", gap: "0.5rem" }}>
          <Text size="1" style={{ color: "rgba(255,255,255,0.58)" }}>{label}</Text>
          <Text size="1" style={{ color: "inherit" }}>{String(value)}</Text>
        </Box>
      ))}
    </Box>
  );
}

export interface DisplayScreenProps {
  slug: string;
  authToken?: string;
  debugShell?: boolean;
  onNavigateToBoardSetup?: (slug: string) => void;
  onNavigateToDashboard?: () => void;
}

function defaultNavigateToBoardSetup(slug: string) {
  window.location.assign(`/boards/${encodeURIComponent(slug)}`);
}

function defaultNavigateToDashboard() {
  window.location.assign("/dashboard");
}

export function DisplayScreen({
  slug,
  authToken,
  debugShell = false,
  onNavigateToBoardSetup,
  onNavigateToDashboard,
}: DisplayScreenProps) {
  const auth = useAuthStore();
  const overlay = useDisplayOverlay(6000);
  const navigateToBoardSetup = onNavigateToBoardSetup ?? defaultNavigateToBoardSetup;
  const navigateToDashboard = onNavigateToDashboard ?? defaultNavigateToDashboard;

  const [feedState, setFeedState] = useState<{
    boardName: string;
    displayItems: FeedItem[];
    timing: SlideTimingSettings;
  }>({ boardName: "", displayItems: [], timing: {} });
  const [quickTimings, setQuickTimings] = useState<QuickTimingSecState>(() =>
    timingSecFromSource(null),
  );
  const [jumpKey, setJumpKey] = useState(0);

  const feedRef = useRef<FeedPayload | null>(null);
  const archiveStateRef = useRef<ArchiveState>(initArchiveState());
  const lastQueueIdsRef = useRef<string>("");

  const canLoadBoardForOverlay = overlay.isVisible && auth.isAuthenticated;
  const { data: board, isLoading: isBoardLoading } = useBoard(slug, canLoadBoardForOverlay);
  const updateBoard = useUpdateBoard(slug);

  const canEditQuickTimings =
    board?.role === "admin" || board?.role === "contributor";

  const buildDisplayItems = useCallback(
    (feed: FeedPayload, archiveState: ArchiveState): FeedItem[] => {
      const queueItems = feed.queue.map(queueItemToFeedItem);
      const archiveItems = archiveWindowToFeedItems(archiveState, feed.archive, feed.board.slug ?? "");
      return [...queueItems, ...archiveItems];
    },
    [],
  );

  const handleFeed = useCallback(
    (feed: FeedPayload) => {
      feedRef.current = feed;

      const boardLang = feed.board.settings?.language as LanguageCode | undefined;
      if (boardLang && SUPPORTED_LANGUAGES.some((l) => l.code === boardLang)) {
        i18n.changeLanguage(boardLang);
      }

      if (archiveStateRef.current.currentWindow.length === 0 && feed.archive.length > 0) {
        archiveStateRef.current = initializeArchiveWindow(
          feed.archive,
          feed.archive_window_size,
          dateSeed(new Date()),
        );
      }

      const displayItems = buildDisplayItems(feed, archiveStateRef.current);
      const newQueueIds = feed.queue.map((q) => q.content_id).join(",");
      const queueChanged = newQueueIds !== lastQueueIdsRef.current;
      lastQueueIdsRef.current = newQueueIds;

      const settings = feed.board.settings ?? {};
      const timing: SlideTimingSettings = {
        messageDurationMs: typeof settings.messageDurationMs === "number" ? settings.messageDurationMs : undefined,
        imageDurationMs: typeof settings.imageDurationMs === "number" ? settings.imageDurationMs : undefined,
        weatherDurationMs: typeof settings.weatherDurationMs === "number" ? settings.weatherDurationMs : undefined,
      };

      setFeedState({ boardName: feed.board.name ?? "", displayItems, timing });

      if (queueChanged) {
        setJumpKey((k) => k + 1);
      }
    },
    [buildDisplayItems],
  );

  const handleCycleComplete = useCallback(() => {
    const feed = feedRef.current;
    if (!feed) return;

    archiveStateRef.current = advanceArchiveWindow(
      archiveStateRef.current,
      feed.archive,
      feed.archive_window_size,
      dateSeed(new Date()),
    );

    const displayItems = buildDisplayItems(feed, archiveStateRef.current);
    setFeedState((prev) => ({ ...prev, displayItems }));
  }, [buildDisplayItems]);

  const { status } = useDisplayStream({ slug, authToken, onFeed: handleFeed });
  const shellDebug = useShellDebug(debugShell);

  useEffect(() => {
    if (!overlay.isVisible) return;

    // Quand l'overlay s'ouvre, on hydrate d'abord depuis le feed en cours,
    // puis depuis le board auth si dispo (plus fiable pour les permissions).
    const source = board?.settings ?? feedState.timing;
    setQuickTimings(timingSecFromSource(source));
  }, [
    board?.settings,
    feedState.timing,
    overlay.isVisible,
  ]);

  useEffect(() => {
    const el = document.documentElement;
    const displayMode = detectDisplayMode();

    if (displayMode === "browser" && el.requestFullscreen && !document.fullscreenElement) {
      el.requestFullscreen().catch(() => {});
    }

    el.classList.add("kiosk-mode");
    return () => {
      el.classList.remove("kiosk-mode");
    };
  }, []);

  useEffect(() => {
    if (!overlay.isVisible) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        overlay.hide();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [overlay.hide, overlay.isVisible]);

  useEffect(() => {
    // Cas principal: PWA relancée sur une URL display devenue invalide
    // (board supprimé). On renvoie l'utilisateur authentifié vers
    // la liste de ses tableaux plutôt que de rester bloqué sur une erreur.
    if (status !== "not_found") return;
    if (!auth.isAuthenticated) return;
    navigateToDashboard();
  }, [auth.isAuthenticated, navigateToDashboard, status]);

  const handleGoToBoardSetup = useCallback(() => {
    overlay.hide();
    navigateToBoardSetup(slug);
  }, [navigateToBoardSetup, overlay.hide, slug]);

  const handleSaveQuickTimings = useCallback(async () => {
    if (!board || !canEditQuickTimings) return;

    await updateBoard.mutateAsync({
      settings: {
        ...board.settings,
        messageDurationMs: quickTimings.messageDurationSec * 1000,
        imageDurationMs: quickTimings.imageDurationSec * 1000,
        weatherDurationMs: quickTimings.weatherDurationSec * 1000,
      },
    });
  }, [board, canEditQuickTimings, quickTimings, updateBoard]);

  const isForbidden = status === "forbidden";

  return (
    <Box
      style={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        height: "100dvh",
        overflow: "hidden",
        backgroundColor: "#000",
        cursor: overlay.isVisible ? "default" : "none",
      }}
      onPointerDown={() => {
        if (!overlay.isVisible) {
          overlay.show();
        }
      }}
    >
      {isForbidden ? (
        <ForbiddenState />
      ) : feedState.displayItems.length === 0 ? (
        <EmptyState boardName={feedState.boardName} />
      ) : (
        <DisplayCarousel
          items={feedState.displayItems}
          timingSettings={feedState.timing}
          onCycleComplete={handleCycleComplete}
          jumpKey={jumpKey}
        />
      )}

      <StatusIndicator status={status} hasContent={feedState.displayItems.length > 0} />
      <ShellDebugOverlay info={shellDebug} />

      <DisplayExitOverlay
        visible={overlay.isVisible}
        boardName={feedState.boardName}
        onClose={overlay.hide}
        onGoToBoardSetup={handleGoToBoardSetup}
        onInteract={overlay.ping}
      >
        <DisplayQuickTimingPanel
          canEdit={canEditQuickTimings}
          isLoading={Boolean(auth.isAuthenticated && isBoardLoading)}
          isSaving={updateBoard.isPending}
          messageDurationSec={quickTimings.messageDurationSec}
          imageDurationSec={quickTimings.imageDurationSec}
          weatherDurationSec={quickTimings.weatherDurationSec}
          onMessageDurationSecChange={(value) =>
            setQuickTimings((prev) => ({ ...prev, messageDurationSec: value }))
          }
          onImageDurationSecChange={(value) =>
            setQuickTimings((prev) => ({ ...prev, imageDurationSec: value }))
          }
          onWeatherDurationSecChange={(value) =>
            setQuickTimings((prev) => ({ ...prev, weatherDurationSec: value }))
          }
          onSave={handleSaveQuickTimings}
        />
      </DisplayExitOverlay>
    </Box>
  );
}
